import { ClockIcon, Cross2Icon } from "@radix-ui/react-icons";
import {
  Auth0AccountRole,
  LocationsService,
  MeetingStudentLimitedResponse,
  MeetingTutorLimitedResponse,
  OrganizationMeetingLocationResponse,
  OrganizationSubjectAdminResponse,
  OrganizationSubjectPublicResponse,
  OrgSubjectsService,
  SessionFrequency,
  StudentLimitedResponse,
  TutorLimitedResponse,
} from "client/openapi";
import { Button, ButtonColor, ButtonSize } from "components/Button";
import { InitialMeetingDraft, MeetingDetails } from "components/MeetingDialog";
import EditMeetingDetails from "components/MeetingDialog/Components/editMeetingDetails";
import {
  AttendeeFields,
  SubjectField,
} from "components/MeetingDialog/Components/MeetingFields";
import RoomsGrid from "components/MeetingDialog/Components/RoomsGrid";
import TutorAvailabilityGrid from "components/MeetingDialog/Components/TutorAvailabilityGrid";
import {
  createMeetings,
  getMeetingDraftError,
  withoutTutorOverrides,
} from "components/MeetingDialog/meetingDraft";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/Tooltip";
import { AttendeesList } from "components/MeetingAttendees";
import { concatenateName } from "util/concatenateName";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import Notifications from "util/notifications";
import { formatDayAndTimeRange } from "./context";

type DraftStudent = MeetingStudentLimitedResponse | StudentLimitedResponse;
type DraftTutor = MeetingTutorLimitedResponse | TutorLimitedResponse;
type DraftSubject =
  | OrganizationSubjectPublicResponse
  | OrganizationSubjectAdminResponse;

/**
 * The compact Book Meeting form in the calendar pop-up. It uses the same
 * pieces and rules as Book Meeting (MeetingFields, EditMeetingDetails,
 * meetingDraft), with the date and time from the calendar selection. The
 * rooms grid can move the time on that day.
 * Mounted only while the pop-up is open.
 */
export default function CompactBookingForm({
  orgId,
  start,
  end,
  onClose,
  onTimeChange,
  onSaved,
  onMoreOptions,
}: {
  orgId: number;
  start: Date;
  end: Date;
  onClose: () => void;
  /** The rooms grid moved the time: the calendar's selection follows. */
  onTimeChange: (start: Date, end: Date) => void;
  onSaved: () => void;
  onMoreOptions: (draft: InitialMeetingDraft) => void;
}) {
  const [draft, setDraft] = useState<MeetingDetails>({
    org_id: orgId,
    start: moment(start).format(),
    duration: moment(end).diff(moment(start), "minutes"),
    ends_on_or_before: undefined,
    name: "",
    location: undefined,
    other_location: "",
    student_ids: [],
    tutor_ids: [],
    // Most bookings are single meetings, so the pop-up starts with Never.
    frequency: SessionFrequency.SINGLE,
    org_subject: undefined,
    locked: false,
    note: undefined,
  });
  const [students, setStudents] = useState<DraftStudent[]>([]);
  const [tutors, setTutors] = useState<DraftTutor[]>([]);
  const [subject, setSubject] = useState<DraftSubject | undefined>();
  const [availableSubjects, setAvailableSubjects] = useState<
    | OrganizationSubjectPublicResponse[]
    | OrganizationSubjectAdminResponse[]
    | undefined
  >();
  const [locationOptions, setLocationOptions] = useState<
    OrganizationMeetingLocationResponse[] | undefined
  >();
  const [saving, setSaving] = useState<boolean>(false);
  // The grid shown in the form's place, opened from the icons by Tutors and Location.
  const [grid, setGrid] = useState<"tutors" | "rooms" | null>(null);

  useEffect(() => {
    let cancelled = false;
    OrgSubjectsService.getSubjectsByOrg({ orgId, active: true })
      .then((subjects) => {
        if (!cancelled) setAvailableSubjects(subjects);
      })
      .catch((err) => console.error("Failed to fetch subjects:", err));
    LocationsService.getLocationsByOrg({ orgId })
      .then((locations) => {
        if (!cancelled) setLocationOptions(locations);
      })
      .catch((err) => console.error("Failed to fetch locations:", err));
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  const updateAttendees = useCallback(
    (userType: Auth0AccountRole, attendees: (DraftStudent | DraftTutor)[]) => {
      if (userType === Auth0AccountRole.ME) {
        const list = attendees as DraftStudent[];
        setStudents(list);
        setDraft((d) => ({
          ...d,
          student_ids: list.map((s) => ("student" in s ? s.student.id : s.id)),
        }));
      } else {
        const list = attendees as DraftTutor[];
        setTutors(list);
        setDraft((d) =>
          withoutTutorOverrides({
            ...d,
            tutor_ids: list.map((t) => ("tutor" in t ? t.tutor.id : t.id)),
          })
        );
      }
    },
    []
  );

  const removeAttendee = useCallback(
    (userType: Auth0AccountRole, id: number) => {
      if (userType === Auth0AccountRole.ME) {
        setStudents((list) =>
          list.filter((s) => ("student" in s ? s.student.id : s.id) !== id)
        );
        setDraft((d) => ({
          ...d,
          student_ids: d.student_ids.filter((sid) => sid !== id),
        }));
      } else {
        setTutors((list) =>
          list.filter((t) => ("tutor" in t ? t.tutor.id : t.id) !== id)
        );
        setDraft((d) =>
          withoutTutorOverrides({
            ...d,
            tutor_ids: d.tutor_ids.filter((tid) => tid !== id),
          })
        );
      }
    },
    []
  );

  const handleSubjectChange = useCallback(
    (option: DraftSubject | undefined) => {
      setSubject(option);
      setDraft((d) => ({ ...d, org_subject: option }));
    },
    []
  );

  const saveError = getMeetingDraftError([draft]);
  const canSave = saveError === "" && !!subject && !saving;

  const save = async () => {
    if (!canSave || !subject) return;
    setSaving(true);
    try {
      await createMeetings([draft], subject.id);
      onSaved();
    } catch (e) {
      Notifications.error(`Error creating meeting: ${e}`);
      setSaving(false);
    }
  };

  const durationEnd = moment(draft.start).add(draft.duration, "minutes");

  // A click in a grid sets the time (and in the rooms grid the room), keeping
  // the length, as in Book Meeting; the calendar's selection follows.
  const pickTime = (pickedStart: string) =>
    onTimeChange(
      moment(pickedStart).toDate(),
      moment(pickedStart).add(draft.duration, "minutes").toDate()
    );

  // The icons swap the form for a grid, which stays open with This meeting
  // moved; closing it returns here. Three days or rooms are in view and the
  // rest scroll sideways: the meeting's week, or every room on its day.
  if (grid === "tutors") {
    return (
      <div className="gutter-booking">
        <TutorAvailabilityGrid
          week
          columnsInView={3}
          orgId={orgId}
          tutors={tutors.map((t) => ({
            id: "tutor" in t ? t.tutor.id : t.id,
            name: concatenateName(t),
          }))}
          start={draft.start}
          duration={draft.duration}
          onPick={(pickedStart) => {
            setDraft((d) => ({
              ...d,
              start: pickedStart,
              room_overrides: undefined,
              tutor_overrides: undefined,
            }));
            pickTime(pickedStart);
          }}
          // No tutor yet: the form's own Tutors field, to pick one here.
          empty={
            <div className="meeting-fields max-w-[16rem]">
              <AttendeesList
                attendees={tutors}
                typeOfAttendees={Auth0AccountRole.ORG_TUTOR}
                linkPrefix="/manage/tutors/"
                editing
                creating
                subject={subject}
                currentMeetingInfo={draft}
                orgId={orgId}
                remove={removeAttendee}
                updateAttendees={updateAttendees}
              />
            </div>
          }
          close={{
            label: "Close tutor availability",
            onClick: () => setGrid(null),
          }}
        />
      </div>
    );
  }

  if (grid === "rooms" && locationOptions) {
    return (
      <div className="gutter-booking">
        <RoomsGrid
          orgId={orgId}
          locations={locationOptions}
          start={draft.start}
          duration={draft.duration}
          locationId={draft.location?.id}
          columnsInView={3}
          onPick={(location, pickedStart) => {
            setDraft((d) => ({
              ...d,
              location,
              other_location: undefined,
              start: pickedStart,
              room_overrides: undefined,
              tutor_overrides: undefined,
            }));
            pickTime(pickedStart);
          }}
          close={{ label: "Close rooms", onClick: () => setGrid(null) }}
        />
      </div>
    );
  }

  return (
    <div className="gutter-booking">
      {/* The title and the time are the pop-up's header, which drags it. */}
      <div className="flex items-start gap-2" data-drag-handle>
        <input
          type="text"
          className="gutter-booking__title"
          placeholder="Add title"
          aria-label="Title"
          value={draft.name ?? ""}
          onChange={(e) => {
            const name = e.target.value;
            setDraft((d) => ({ ...d, name }));
          }}
        />
        <button
          type="button"
          className="gutter-booking__close"
          aria-label="Close"
          onClick={onClose}
        >
          <Cross2Icon width={18} height={18} />
        </button>
      </div>

      <p
        className="gutter-booking__when mt-2 mb-4 flex items-center gap-2 text-sm text-gray-700"
        data-drag-handle
      >
        <ClockIcon className="flex-shrink-0" />
        {formatDayAndTimeRange(draft.start, durationEnd.toDate())}
      </p>

      <EditMeetingDetails
        compact
        id={0}
        tutors={tutors}
        currentMeetingDetails={draft}
        orginalMeetingDetails={draft}
        // A new weekly time for the tutors moves the calendar's selection too.
        saveMeetingDetails={(meeting) => {
          setDraft(meeting);
          if (meeting.start !== draft.start) pickTime(meeting.start);
        }}
        locationOptions={locationOptions}
        creating={true}
        onShowRooms={() => setGrid("rooms")}
        // Book Meeting's order: tutors and students above Location, subject beside it.
        attendees={
          <AttendeeFields
            tutors={tutors}
            students={students}
            creating={true}
            subject={subject}
            currentMeetingInfo={draft}
            orgId={orgId}
            removeAttendee={removeAttendee}
            updateAttendees={updateAttendees}
            onSubjectChange={handleSubjectChange}
            onShowTutorAvailability={() => setGrid("tutors")}
          />
        }
        subject={
          <SubjectField
            availableSubjects={availableSubjects}
            subject={subject}
            currentMeetingInfo={draft}
            onChange={handleSubjectChange}
          />
        }
      />

      <div className="mt-5 flex items-center justify-between gap-3">
        <button
          type="button"
          className="text-sm font-bold text-green-600 hover:underline"
          onClick={() =>
            onMoreOptions({ meeting: draft, students, tutors, subject })
          }
        >
          More options
        </button>
        <Tooltip>
          <TooltipTrigger>
            <span>
              <Button
                size={ButtonSize.SMALL}
                color={canSave ? ButtonColor.GREEN : ButtonColor.GRAY}
                disabled={!canSave}
                onClick={save}
                extraClasses={`min-w-[6rem]${
                  saveError === "" ? "" : " pointer-events-none"
                }`}
              >
                {saving ? (
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                ) : (
                  <>Save</>
                )}
              </Button>
            </span>
          </TooltipTrigger>
          {saveError !== "" && <TooltipContent>{saveError}</TooltipContent>}
        </Tooltip>
      </div>
    </div>
  );
}
